import { defineStore } from 'pinia'
import type { Permission, RolePermissionMatrix } from '@/types/permission'
import type { UserRole } from '@/types/user'
import { DEFAULT_ROLE_PERMISSIONS } from '@/auth/permissions'
import { rolesService } from '@/services/roles.service'
import { useAuthStore } from './auth.store'

/**
 * Holds the tenant's role → permissions matrix, used by the Echipă editor and
 * as the fallback answer to "may I?". Starts from the spec defaults so the
 * first render is already permission-aware.
 */
export const usePermissionsStore = defineStore('permissions', {
  state: () => ({
    matrix: DEFAULT_ROLE_PERMISSIONS as RolePermissionMatrix,
    loaded: false,
  }),
  getters: {
    currentRole(): UserRole | null {
      return useAuthStore().user?.role ?? null
    },
    granted(): Set<Permission> {
      // The API is authoritative about what this session may do; the local
      // matrix is the fallback for mock mode and for the very first paint.
      const fromServer = useAuthStore().user?.permissions
      if (fromServer) return new Set(fromServer as Permission[])

      const role = this.currentRole
      return new Set(role ? this.matrix[role] : [])
    },
  },
  actions: {
    can(permission: Permission): boolean {
      return this.granted.has(permission)
    },
    async load() {
      try {
        this.matrix = await rolesService.getMatrix()
      } catch {
        // Only owners/admins may read the matrix; everyone else keeps the
        // defaults, which is fine because `granted` uses their own permissions.
      }
      this.loaded = true
    },
    async updateRole(role: UserRole, permissions: Permission[]) {
      this.matrix = await rolesService.updateRole(role, permissions)
    },
    async resetToDefaults() {
      this.matrix = await rolesService.resetToDefaults()
    },
  },
})
